import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import StarIcon from '@mui/icons-material/Star';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import { Box, IconButton, ListItemText, MenuItem, useTheme } from '@mui/material';
import React from 'react';

import { SavedReport } from './ReportTabs.props';

export interface ReportMenuItemProps {
  report: SavedReport;
  onPin: (code: string) => void;
  onSetDefault?: (report: SavedReport) => void;
  onRemoveDefault?: (report: SavedReport) => void;
}

/**
 * A report row in the `+` menu. Clicking the row pins it; the star sets or clears
 * the page default.
 *
 * The set-default star is revealed on hover, but it stays **mounted and
 * focusable** — it used to be conditionally rendered on `hovered`, which meant a
 * keyboard user could never reach it at all. Revealing it on `:focus-within` too
 * keeps it visible once tabbed to, so it isn't operated blind.
 */
export const ReportMenuItem: React.FC<ReportMenuItemProps> = ({
  report,
  onPin,
  onSetDefault,
  onRemoveDefault,
}) => {
  const theme = useTheme();
  const showSetDefault = onSetDefault && !report.isDefault;

  return (
    <MenuItem
      onClick={() => onPin(report.code)}
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 1,
        // Opacity rather than conditional rendering: the controls keep their
        // place in the tab order and the row doesn't reflow when revealed.
        '&:hover .report-row-reveal, &:focus-within .report-row-reveal': { opacity: 1 },
      }}
    >
      <ListItemText primary={report.name} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
        {report.isDefault && onRemoveDefault && (
          <IconButton
            size="small"
            aria-label={`Remove ${report.name} as default`}
            onClick={(e) => {
              e.stopPropagation();
              onRemoveDefault(report);
            }}
            sx={{
              p: 0.25,
              color: theme.palette.warning.main,
              '&:hover': { color: 'text.secondary' },
            }}
          >
            <StarIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}
        {showSetDefault && (
          <IconButton
            className="report-row-reveal"
            size="small"
            aria-label={`Set ${report.name} as default`}
            onClick={(e) => {
              e.stopPropagation();
              onSetDefault(report);
            }}
            sx={{
              p: 0.25,
              opacity: 0,
              transition: 'opacity 0.15s ease',
              color: 'text.secondary',
              '&:hover': { color: theme.palette.warning.main },
              '&:focus-visible': { opacity: 1 },
            }}
          >
            <StarOutlineIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}
        <PushPinOutlinedIcon
          className="report-row-reveal"
          aria-hidden
          sx={{
            fontSize: 16,
            color: 'text.secondary',
            opacity: 0,
            transition: 'opacity 0.15s ease',
          }}
        />
      </Box>
    </MenuItem>
  );
};
